import { Inject, Injectable, OnDestroy } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { fromEvent, merge, Subscription, timer } from 'rxjs';
import { filter, skip, switchMap, take, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import { AuthState, AuthStatus } from '../../../auth/auth.model';
import { AuthService } from '../../../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { useDispatcher, useStore } from '../../../store.decorator';
import { EventCentreHookService, Item } from '../core';
import { HubMethods } from '../core/hub-methods.model';
import { SocketService } from '../core/socket.service';
import { useSelector } from '../store-use-selector';

import { UNREAD_CATEGORY_ID } from './const/unread-category';
import { EventCentreDialogComponent } from './event-centre-dialog.component';
import { eventCentreDialogReducer } from './event-centre-dialog.reducer';
import { EventCentreDialogState } from './event-centre-dialog.state';
import {
    markLastOpen,
    shouldOpenSinceLastOpen,
} from './function/last-open.function';
import { ViewCategory } from './model/view-category.model';
import { ViewItem } from './model/view-item.model';
import { receiveItems } from './state/receive-items.action';
import { reload } from './state/reload.action';
import { reset } from './state/reset.action';
import { seeItems } from './state/see-items.action';
import { selectCategory } from './state/select-category.action';
import { updateItems } from './state/update-items.action';

@Injectable({ providedIn: 'root' })
@TakeUntilDestroy()
export class EventCentreDialogService implements OnDestroy {
    state: EventCentreDialogState;

    // TODO refactor. authService uses the same prefix
    private readonly _storageKeyPrefix: string;

    private _ref: CmpsDialogRef<EventCentreDialogComponent, any>;
    private _refSub: Subscription;

    get isOpen(): boolean {
        return this._ref != null && !this._ref.closed;
    }

    constructor(
        private readonly _authService: AuthService,
        private readonly _dialog: CmpsDialog,
        private readonly _socketService: SocketService,
        private readonly _hookService: EventCentreHookService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        this._storageKeyPrefix = this._appConfig.OAUTH.AUTHORIZATION_ENDPOINT;

        this._bindAuth();

        this._setupBindings();

        useStore(this, 'notification.eventCentre', eventCentreDialogReducer);

        this._checkExtensions();

        this._checkUnreadOnLogin();
    }

    ngOnDestroy(): void {
        this.close();
    }

    open(categoryId: string): void {
        useDispatcher(this, selectCategory({ categoryId }));

        markLastOpen(this._storageKeyPrefix);

        if (this.isOpen) {
            return;
        }
        this._refSub?.unsubscribe();
        this._ref = this._dialog.open(EventCentreDialogComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-event-centre-dialog--overlay-class',
            portalClass: 'cmps-event-centre-dialog--portal-class',
            portalWrapperClass:
                'cmps-event-centre-dialog--portal-wrapper-class',
            disableBackdrop: true,
            canClose: () => {
                /* TODO fix uikit auto-close for mouseup bubbling to document/window when nested in overlays */
                const filterHdl = document.querySelector(
                    '.cmps-event-centre-search-filter--filter',
                );
                const ret2 = !filterHdl?.contains(
                    mouseUpEvt.target as HTMLElement,
                );
                const sortMenuHdl = document.querySelector(
                    '.cmps-event-centre-search-filter--sort-menu',
                )?.parentElement?.parentElement; // .cmps-menu
                const ret3 = !sortMenuHdl?.contains(
                    mouseUpEvt.target as HTMLElement,
                );
                const result = ret2 && ret3;

                if (result === true) {
                    this._hookService.willClose();
                }

                return result;
            },
            placement: 'top-right',
        });
        const mousePos = { x: null, y: null };
        let mouseUpEvt: MouseEvent = null;
        this._refSub = merge(
            this._ref.result.pipe(
                tap(() => {
                    this._ref = null;
                }),
            ),
            fromEvent<MouseEvent>(window, 'mousedown').pipe(
                tap((evt) => {
                    mousePos.x = evt.clientX;
                    mousePos.y = evt.clientY;
                }),
            ),
            fromEvent<MouseEvent>(window, 'mouseup').pipe(
                tap((evt) => {
                    mouseUpEvt = evt;
                }),
            ),
        )
            .pipe(takeUntilDestroy(this))
            .subscribe();

        this._hookService.didOpen(false, this.state.category, this);
    }

    close(): void {
        // must call ref.close() before refSub.unsubscribe() so that ref.result() completes
        this._ref?.close();
        this._refSub?.unsubscribe();
    }

    toggle(categoryId: string): void {
        !this.isOpen ? this.open(categoryId) : this.close();
    }

    updateItem(item: ViewItem, read: boolean, deleted: boolean): void {
        useDispatcher(
            this,
            updateItems({
                items: [item],
                patch: { read, delete: deleted },
            }),
        );
    }

    private _bindAuth(): void {
        let wasLoggedOut =
            this._authService.authStatus.value !== AuthStatus.AUTHENTICATED;
        this._authService.authStatus
            .pipe(
                tap((authStatus) => {
                    if (
                        [
                            AuthState.LOGOUT,
                            AuthState.TIMEOUT,
                            AuthState.LOST,
                        ].includes(this._authService.authState) &&
                        authStatus !== AuthStatus.AUTHENTICATED
                    ) {
                        this.close();
                        useDispatcher(this, reset({}));
                        wasLoggedOut = true;
                    }

                    if (
                        [
                            AuthState.LOCAL_STORAGE,
                            AuthState.INTERACTIVE,
                            AuthState.NON_INTERACTIVE,
                        ].includes(this._authService.authState) &&
                        authStatus === AuthStatus.AUTHENTICATED
                    ) {
                        if (wasLoggedOut) {
                            useDispatcher(this, reload({}));
                        }
                        wasLoggedOut = false;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _setupBindings(): void {
        if (this._socketService.created) {
            this._bind();
        }
        this._socketService.didCreate
            .pipe(
                tap((_socketService) => {
                    this._bind();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _bind(): void {
        this._socketService.on(HubMethods.OnItem, (payload: Item) => {
            useDispatcher(this, receiveItems({ items: [payload] }));
        });
    }

    private _checkUnreadOnLogin(): void {
        this._authService.authStatus
            .pipe(
                skip(1),
                filter((authStatus) => {
                    return (
                        (this._authService.authState ===
                            AuthState.INTERACTIVE ||
                            shouldOpenSinceLastOpen(this._storageKeyPrefix)) &&
                        authStatus === AuthStatus.AUTHENTICATED
                    );
                }),
                switchMap(() => {
                    return useSelector<ViewCategory[] | null | undefined>(
                        (state) =>
                            (
                                state?.notification
                                    ?.eventCentre as EventCentreDialogState
                            )?.categories,
                    ).pipe(
                        filter(([_, state]) => {
                            return state?.[0]?.unreadCount > 0;
                        }),
                        take(1),
                    );
                }),
                switchMap(() => {
                    return timer(500);
                }),
                tap(() => {
                    this.open(UNREAD_CATEGORY_ID);

                    // open() calls selectCategory() action, which is synchronous
                    this._hookService.didOpen(true, this.state.category, this);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _checkExtensions(): void {
        useSelector<ViewItem[]>(
            (state) =>
                (state?.notification?.eventCentre as EventCentreDialogState)
                    ?.items,
        )
            .pipe(
                tap(() => {
                    const items =
                        this.state.items?.filter(
                            (e) =>
                                !this.state.seenItems?.includes(
                                    e.eventCentreItemId,
                                ),
                        ) ?? [];
                    useDispatcher(this, seeItems({}));
                    this._hookService.itemsChanged(items);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
